import React, { useEffect, useState } from "react";
import { Eye, Loader2, LogIn, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { PREVIEW_KEY, apiClient, exitLoginAs, exitPreview, formatApiError, useAuth } from "@/lib/auth";
import { VENDOR_IMP_KEY } from "@/lib/vendorApi";
import { Btn } from "@/components/console/kit";

const useIsSa = () => { const { user } = useAuth(); return !user?.preview_by && !user?.login_as_by && (user?.role === "super_admin" || !!user?.is_super_admin); };

/** Super Admin: open a read-only preview of the platform exactly as this staff member or partner sees it, in a new tab. */
export const PreviewAsButton = ({ kind, id, name, className = "" }) => {
  const isSa = useIsSa();
  const [busy, setBusy] = useState(false);
  if (!isSa) return null;
  const open = async () => {
    const tab = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/access/preview/${kind}/${id}`);
      if (tab) tab.location.href = `/preview-start#${data.token}`; else window.open(`/preview-start#${data.token}`, "_blank");
    } catch (e) { tab?.close(); toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return <Btn tone="muted" onClick={open} disabled={busy} className={className} data-testid={`preview-as-${kind}-${id}`} title={`See the platform as ${name} (read only)`}>
    {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview</Btn>;
};

/** Super Admin: open a full, acting session as this partner or vendor in a new tab. Ends only on Exit. */
export const LoginAsButton = ({ kind, id, name, className = "" }) => {
  const isSa = useIsSa();
  const [busy, setBusy] = useState(false);
  if (!isSa) return null;
  const open = async () => {
    if (!window.confirm(`Log in as ${name}? You'll act as them in a new tab, and every change is recorded as done by you as ${name}.`)) return;
    const tab = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/access/login-as/${kind}/${id}`);
      const url = `${kind === "contract_vendor" ? "/vendor-login-as" : "/preview-start"}#${data.token}`;
      if (tab) tab.location.href = url; else window.open(url, "_blank");
    } catch (e) { tab?.close(); toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return <Btn tone="muted" onClick={open} disabled={busy} className={className} data-testid={`login-as-${kind}-${id}`} title={`Act as ${name} in a new tab`}>
    {busy ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />} Login As</Btn>;
};

/** /vendor-login-as#<token>: the vendor session lives in this tab only, then the vendor portal opens. */
export function VendorLoginAsStart() {
  useEffect(() => {
    const t = window.location.hash.slice(1);
    if (t) sessionStorage.setItem(VENDOR_IMP_KEY, t);
    window.location.replace("/vendor");
  }, []);
  return <div className="min-h-screen bg-[#0B0F19] grid place-items-center text-white/60 text-sm" data-testid="vendor-login-as-start">Opening vendor portal…</div>;
}

export { exitLoginAs };

export const LoginAsBar = ({ name, onExit }) => (
  <div className="sticky top-0 z-[100] w-full bg-[#d4a017] text-black px-4 py-2 flex flex-wrap items-center gap-3 text-sm" data-testid="login-as-banner">
    <LogIn size={15} />
    <span className="font-semibold" data-testid="login-as-banner-name">Logged in as {name}</span>
    <span className="text-black/70">— you're acting as them; every change is recorded as done by you</span>
    <button type="button" onClick={onExit} data-testid="login-as-exit" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-black text-[#F4D88A] px-3 py-1 text-xs font-semibold hover:bg-black/80 transition-colors"><X size={12} /> Exit</button>
  </div>
);

/** /preview-start#<token>: keep the preview token in this tab only, then open the desktop. */
export function PreviewStart() {
  useEffect(() => {
    const t = window.location.hash.slice(1);
    if (t) sessionStorage.setItem(PREVIEW_KEY, t);
    window.location.replace("/admin");
  }, []);
  return <div className="min-h-screen bg-[#0B0F19] grid place-items-center text-white/60 text-sm" data-testid="preview-start">Opening preview…</div>;
}

/** Gold bar on every screen of a preview tab. */
export function PreviewBanner() {
  const { user } = useAuth();
  if (user?.login_as_by && sessionStorage.getItem(PREVIEW_KEY)) {
    return <LoginAsBar name={user.login_as_name || user.partner_name || user.name} onExit={async () => { await exitLoginAs(sessionStorage.getItem(PREVIEW_KEY)); exitPreview(); }} />;
  }
  if (!user?.preview_by || !sessionStorage.getItem(PREVIEW_KEY)) return null;
  return (
    <div className="sticky top-0 z-[100] w-full bg-[#d4a017] text-black px-4 py-2 flex flex-wrap items-center gap-3 text-sm" data-testid="preview-banner">
      <Eye size={15} />
      <span className="font-semibold" data-testid="preview-banner-name">Previewing as {user.partner_name || user.name}</span>
      <span className="text-black/70">{user.role_title || (user.role === "partner_user" ? "Partner" : user.role === "super_admin" ? "Super admin" : "Staff")} · read only — nothing can be changed</span>
      <button type="button" onClick={exitPreview} data-testid="preview-exit" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-black text-[#F4D88A] px-3 py-1 text-xs font-semibold hover:bg-black/80 transition-colors"><X size={12} /> Exit preview</button>
    </div>
  );
}

export const TeaPhoneTag = ({ id }) => (
  <span data-testid={id ? `tea-phone-tag-${id}` : "tea-phone-tag"} className="inline-flex items-center gap-1 rounded-full border border-sky-400/50 bg-sky-400/10 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] text-sky-300 align-middle"><Phone size={9} /> TEA Phone</span>
);

/** Super Admin: mark that a resident carries one of TEA's phones. */
export const TeaPhoneToggle = ({ rid, on, onChanged }) => {
  const isSa = useIsSa();
  const [busy, setBusy] = useState(false);
  if (!isSa) return null;
  const flip = async () => {
    setBusy(true);
    try { await apiClient.put(`/access/tea-phone/${rid}`, { on: !on }); toast.success(on ? "TEA Phone tag removed" : "Marked as having a TEA phone"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return <Btn tone="muted" onClick={flip} disabled={busy} data-testid={`tea-phone-toggle-${rid}`}><Phone size={12} /> {on ? "Remove TEA Phone" : "Has TEA Phone"}</Btn>;
};
